import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  Stack,
  HStack,
  Switch,
  Radio,
  RadioGroup,
  Tag,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Progress,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { EditIcon, ChevronDownIcon, RepeatIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { SlidersHorizontalIcon } from "lucide-react";
import * as Sentry from "@sentry/react";
import _ from "lodash";

import MoblyzeApolloClient from "../../services/MoblyzeApolloClient";
import { AGENCY_CANDIDATE_TRIAGE_SEND_INVITE_MUTATION } from "../../graphql/jobs";

import JobRequirementsEditor from "../JobRequirementsEditor";

import { ReactComponent as ArrowRight } from "../../assets/icons/right-arrow.svg";
import { ReactComponent as Like } from "../../assets/icons/like.svg";
import { ReactComponent as Dislike } from "../../assets/icons/dislike.svg";
import {
  CandidateTriageStatusApprovedByClient,
  CandidateTriageStatusApprovedByRecruiter,
  CandidateTriageStatusPassedByClient,
} from "../../graphql/jobs";
import colors from "../../helpers/colors";
import { candidateMatchesDeepLinkId } from "../../helpers/utils";
import ProfileImage from "../common/ProfileImage";
import FavouriteIcon from "../common/FavouriteIcon";
import JobInvited from "../common/JobInvited";
import ImportedInvited from "../common/ImportedInvited";
import MoblyzeUserLogo from "../common/MoblyzeUserLogo";
import AvailabilityLine from "../common/AvailabilityLine";
import RecommendedNote, {
  RECOMMENDED_ACCENT,
  RecommendedStar,
  isRecommended,
} from "./RecommendedNote";
import { AgencyOnFavourite } from "./AgencyShortlistActions";
import { useBoolFlag, FLAGS } from "../../services/featureFlags";
import {
  AVAILABILITY_FILTER_ANY,
  AVAILABILITY_FILTER_BY_JOB_START,
  AVAILABILITY_FILTER_NOW,
  matchesAvailability,
} from "../../helpers/availability";
import { formatStartDate } from "../../helpers/strings";

// The first ~10% of the search covers ~90% of the valuable candidates, so the
// bar should feel mostly full well before the backend is done. The curve is
// then lifted into [0.05, 1] so it's visible immediately at progress = 0:
// f(0) = 0.05, f(0.1) ≈ 0.905, f(0.2) ≈ 0.9905, f(1) = 1.
function displayedMatchingProgress(progress) {
  return 0.05 + 0.95 * (1 - Math.pow(0.1, 10 * progress));
}

// Recommended candidates (Suggested list only) always come first, whatever
// their match score. Stable: each group keeps the order it came in.
function recommendedFirst(items) {
  return [
    ...items.filter((item) => isRecommended(item.candidateReport)),
    ...items.filter((item) => !isRecommended(item.candidateReport)),
  ];
}

function CandidateListItem({
  candidateReportList = [],
  candidateTriageList = [],
  select,
  setSelect,
  isClient,
  showFavourite = false,
  showRejectedLabel = false,
  markViewedByRecruiterType,
  refetch,
  criteriaCategories = [],
  moblyzeJobId,
  matchingProgress,
  showCandidateId,
  allowEditCriteria = true,
  defaultRequiredFiltersActive = false,
  // Offer the availability filter. Only callers whose list query selects the
  // availability fields should pass this: on a list without them the filter
  // would silently match nobody.
  showAvailabilityFilter = false,
  jobStartDate,
}) {
  const {
    isOpen: isEditReqsOpen,
    onOpen: onEditReqsOpen,
    onClose: onEditReqsClose,
  } = useDisclosure();
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const [moblyzeUsersOnly, setMoblyzeUsersOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [availabilityFilter, setAvailabilityFilter] = useState(AVAILABILITY_FILTER_ANY);
  const [criterionFilters, setCriterionFilters] = useState(() => {
    if (!defaultRequiredFiltersActive) return [];
    return criteriaCategories
      .flatMap((category) => category.criteria)
      .filter((req) => req.isRequired)
      .map((req) => req.id);
  });
  const [sortedCandidateReportList, setSortedCandidateReportList] = useState([]);
  const [invitedCandidateReportList, setInvitedCandidateReportList] = useState([]);
  const initializedRef = useRef(criteriaCategories.length > 0);
  const toast = useToast();

  useEffect(() => {
    if (criteriaCategories.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      if (!defaultRequiredFiltersActive) return;
      setCriterionFilters(
        criteriaCategories
          .flatMap((category) => category.criteria)
          .filter((req) => req.isRequired)
          .map((req) => req.id)
      );
    }
  }, [criteriaCategories, defaultRequiredFiltersActive]);

  useEffect(() => {
    if (!showFavourite) {
      setFavouritesOnly(false);
    }
  }, [showFavourite]);

  const hasActiveFilters =
    favouritesOnly ||
    moblyzeUsersOnly ||
    criterionFilters.length > 0 ||
    availabilityFilter !== AVAILABILITY_FILTER_ANY;

  // Counts come from the whole current list, like the criterion counts, so
  // each option shows up front how much choosing it would hide. "Free by job
  // start" is only offered when the job has a start date to compare against.
  const availabilityOptions = useMemo(() => {
    if (!showAvailabilityFilter) return [];
    const profiles = [...candidateReportList, ...candidateTriageList]
      .filter((source) => source?.candidate)
      .map((source) => source.candidate.candidateProfile);
    const startsOn = jobStartDate ? new Date(jobStartDate) : null;
    const hasStart = startsOn !== null && !Number.isNaN(startsOn.getTime());
    const countFor = (filter) =>
      profiles.filter((profile) => matchesAvailability(filter, profile, jobStartDate))
        .length;
    const options = [
      { value: AVAILABILITY_FILTER_ANY, label: "Any", count: profiles.length },
      {
        value: AVAILABILITY_FILTER_NOW,
        label: "Available now",
        count: countFor(AVAILABILITY_FILTER_NOW),
      },
    ];
    if (hasStart) {
      options.push({
        value: AVAILABILITY_FILTER_BY_JOB_START,
        label: `Free by job start (${formatStartDate(jobStartDate)})`,
        count: countFor(AVAILABILITY_FILTER_BY_JOB_START),
      });
    }
    return options;
  }, [showAvailabilityFilter, candidateReportList, candidateTriageList, jobStartDate]);

  // If the job loses its start date (or the filter is hidden), don't leave a
  // filter applied that the drawer no longer shows.
  useEffect(() => {
    if (
      availabilityFilter !== AVAILABILITY_FILTER_ANY &&
      !availabilityOptions.some((option) => option.value === availabilityFilter)
    ) {
      setAvailabilityFilter(AVAILABILITY_FILTER_ANY);
    }
  }, [availabilityFilter, availabilityOptions]);

  const criterionMatchCounts = useMemo(() => {
    const counts = {};
    const sources = [...candidateReportList, ...candidateTriageList];
    for (const source of sources) {
      if (!source?.matchingCriteriaCategories) continue;
      for (const mt of source.matchingCriteriaCategories) {
        for (const req of mt.requirements || []) {
          if (req.matches) {
            counts[req.criterionId] = (counts[req.criterionId] || 0) + 1;
          }
        }
      }
    }
    return counts;
  }, [candidateReportList, candidateTriageList]);

  const [sortedCandidateTriageList, setSortedCandidateTriageList] = useState([]);

  const {
    isOpen: isInviteAllOpen,
    onOpen: onInviteAllOpen,
    onClose: onInviteAllClose,
  } = useDisclosure();
  const [inviteAllProgress, setInviteAllProgress] = useState(0);
  const [isInviting, setIsInviting] = useState(false);

  // External (pending sign-up) candidates can't be invited until the
  // external-candidate invite flag flips on (the API rejects it while off), so
  // exclude them from both the "Invite All" batch and its count. When the flag
  // is on they're invitable like anyone else.
  const externalInviteEnabled = useBoolFlag(FLAGS.EXTERNAL_CANDIDATE_JOB_INVITE);
  const invitableCandidateReportList = useMemo(
    () =>
      sortedCandidateReportList.filter(
        (item) => externalInviteEnabled || !item.candidate?.isExternal
      ),
    [sortedCandidateReportList, externalInviteEnabled]
  );

  const onInviteAll = async () => {
    const candidates = invitableCandidateReportList.map((item) => item.candidate);
    if (!candidates.length || !moblyzeJobId) return;
    setIsInviting(true);
    setInviteAllProgress(0);
    let completed = 0;
    for (const candidate of candidates) {
      try {
        await MoblyzeApolloClient.mutate({
          mutation: AGENCY_CANDIDATE_TRIAGE_SEND_INVITE_MUTATION,
          variables: { moblyzeJobId, candidateProfileId: candidate.candidateProfileId },
        });
      } catch (e) {
        Sentry.captureException(e);
      }
      completed += 1;
      setInviteAllProgress(completed / candidates.length);
    }
    setIsInviting(false);
    onInviteAllClose();
    if (refetch) refetch();
    toast({ title: `${completed} candidate${completed !== 1 ? "s" : ""} invited`, status: "success" });
  };

  const totalCandidates =
    (candidateReportList?.length || 0) + (candidateTriageList?.length || 0);
  const filteredCandidates =
    sortedCandidateReportList.length +
    invitedCandidateReportList.length +
    sortedCandidateTriageList.length;

  useEffect(() => {
    if (candidateReportList.length === 0) {
      // The report list is the agency-view source; when it's empty (e.g.,
      // ClientShortlist passes the default []) bail out so we don't fight the
      // triage-list effect below over `select`.
      return;
    }
    let newList = candidateReportList
      ?.map((candidateReport) => {
        if (!candidateReport || !candidateReport.candidate) {
          return null;
        }
        const candidate = candidateReport.candidate;

        return {
          candidateReport,
          candidate,
          matchScore: candidateReport.matchScore ?? 0,
        };
      })
      .filter((item) => {
        if (!item) return false;
        if (favouritesOnly && !item.candidateReport.favourite) return false;
        if (moblyzeUsersOnly && item.candidate?.isExternal) return false;
        if (
          !matchesAvailability(
            availabilityFilter,
            item.candidate?.candidateProfile,
            jobStartDate
          )
        ) {
          return false;
        }

        if (criterionFilters.length > 0) {
          const matchingCategories = item.candidateReport.matchingCriteriaCategories || [];
          const allMatching = criterionFilters.every((reqId) => {
            for (const mt of matchingCategories) {
              const req = mt.requirements?.find((r) => r.criterionId === reqId);
              if (req && req.matches) return true;
            }
            return false;
          });
          if (!allMatching) return false;
        }

        return true;
      })
      .sort((a, b) => b.matchScore - a.matchScore);
    // Recommended first, so the default selection is the top recommended
    // candidate, matching what the list shows first.
    newList = recommendedFirst(newList);

    setSortedCandidateReportList(
      newList.filter((item) => !item.candidateReport?.invited)
    );
    setInvitedCandidateReportList(
      newList.filter((item) => item.candidateReport?.invited)
    );

    // If the currently-selected candidate isn't in the filtered list, reconcile
    // to either the candidate referenced by ?showCandidate= (so cross-tab deep
    // links land on the right person) or the first filtered candidate. Skip
    // when filters exclude everyone so we don't clobber a still-valid pick.
    const selectInList =
      select?.candidateProfileId &&
      newList.some((item) => item.candidate?.candidateProfileId === select.candidateProfileId);
    if (newList.length > 0 && !selectInList) {
      const urlMatch = showCandidateId
        ? newList.find((item) => candidateMatchesDeepLinkId(item.candidate, showCandidateId))
        : null;
      setSelect(urlMatch?.candidate || newList[0].candidate);
    }
  }, [
    candidateReportList,
    favouritesOnly,
    moblyzeUsersOnly,
    criterionFilters,
    availabilityFilter,
    jobStartDate,
    select?.candidateProfileId,
    setSelect,
    showCandidateId,
  ]);

  useEffect(() => {
    if (candidateTriageList.length === 0) {
      // The triage list is the client-view source; when it's empty (e.g.,
      // AgencyShortlist passes []) bail out so we don't clear the agency
      // view's `select`, which lives in the report-list namespace.
      return;
    }
    const newList = candidateTriageList
      ?.map((candidateTriage) => {
        if (!candidateTriage || !candidateTriage.candidate) {
          return null;
        }
        return {
          candidateTriage,
          candidate: candidateTriage.candidate,
          matchScore: candidateTriage.matchScore ?? 0,
        };
      })
      .filter((item) => {
        if (!item) return false;
        if (moblyzeUsersOnly && item.candidate?.isExternal) return false;
        if (
          !matchesAvailability(
            availabilityFilter,
            item.candidate?.candidateProfile,
            jobStartDate
          )
        ) {
          return false;
        }
        if (criterionFilters.length > 0) {
          const matchingCategories =
            item.candidateTriage.matchingCriteriaCategories || [];
          const allMatching = criterionFilters.every((reqId) => {
            for (const mt of matchingCategories) {
              const req = mt.requirements?.find((r) => r.criterionId === reqId);
              if (req && req.matches) return true;
            }
            return false;
          });
          if (!allMatching) return false;
        }
        return true;
      })
      .sort((a, b) => b.matchScore - a.matchScore);

    setSortedCandidateTriageList(newList || []);

    // Keep the client-side selection in sync with the filtered triage list
    // when filters change. Selection IDs here are CandidateTriage IDs, not
    // candidate IDs, because that's what ClientShortlist's state tracks.
    const selectInList =
      select?.id &&
      newList?.some((item) => item.candidateTriage.id === select.id);
    if (newList?.length && !selectInList) {
      setSelect(newList[0].candidateTriage);
    }
  }, [
    candidateTriageList,
    moblyzeUsersOnly,
    criterionFilters,
    availabilityFilter,
    jobStartDate,
    select?.id,
    setSelect,
  ]);

  const renderClientShortlistStatus = ({
    candidate,
    candidateTriage,
    candidateReport,
  }) => {
    const rightArrow = (
      <Flex height="100%" alignItems="center">
        <Icon as={ArrowRight} />
      </Flex>
    );

    if (!isClient) {
      // render for recruiter

      if (!!candidateReport && markViewedByRecruiterType !== "") {
        // render for recruiter
        if (candidateReport?.viewedByRecruiter === false) {
          return (
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: colors.brandBlue,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            />
          );
        }
      }

      const isImported = !!candidate?.isExternal;
      return (
        <Fragment>
          <ImportedInvited isImported={isImported} />
          <MoblyzeUserLogo isMoblyzeUser={!isImported} />
          <JobInvited invited={candidateReport?.invited} />
          {showFavourite && (
            <FavouriteIcon
              favourite={candidateReport?.favourite}
              fontSize="20px"
              marginRight="10px"
              onClick={(e) => {
                e.stopPropagation();
                AgencyOnFavourite({
                  toast: toast,
                  candidateReport,
                  refetch: refetch,
                });
              }}
            />
          )}
          {rightArrow}
        </Fragment>
      );
    }

    return (
      <Fragment>
        {candidateTriage?.status === CandidateTriageStatusApprovedByClient && (
          <Flex height="100%" alignItems="center">
            <Icon fontSize="36px" marginRight="20px" as={Like} />
          </Flex>
        )}
        {candidateTriage?.status === CandidateTriageStatusPassedByClient && (
          <Flex height="100%" alignItems="center">
            <Icon fontSize="36px" marginRight="20px" as={Dislike} />
          </Flex>
        )}
        {candidateTriage?.status ===
          CandidateTriageStatusApprovedByRecruiter && (
          <Flex height="100%">
            <Text
              position="absolute"
              right="24px"
              fontSize="12px"
              fontWeight="400"
              fontStyle="italic"
              lineHeight="16px"
              color={colors.charcoalText}
              pr="5"
            >
              Review Candidate
            </Text>
          </Flex>
        )}
        {candidateTriage?.id === select?.id && rightArrow}
      </Fragment>
    );
  };

  const renderCandidateReportListItem = ({
    candidate,
    candidateReport,
  }) => {
    if (!candidate) return null;

    // Pending sign-ups get reduced opacity + italic name so they're
    // visually distinguishable from signed-up candidates without changing
    // the row's place in the score-sorted list. They're fully selectable:
    // the profile-keyed detail view works for them.
    //
    // The dim is applied to the photo + name only (not the whole row) so the
    // status cluster's Bullhorn badge stays fully opaque — CSS opacity creates
    // a stacking context, so a row-level dim can't be overridden per child.
    const isImported = !!candidate.isExternal;
    const importedDim = isImported ? 0.65 : 1;
    // Recommended candidates get an accent edge (kept when selected) and a
    // star by the name.
    const recommended = isRecommended(candidateReport);
    const isSelected = candidate.candidateProfileId === select?.candidateProfileId;

    return (
      <Flex
        key={candidate.candidateProfileId}
        cursor="pointer"
        onClick={() => setSelect(candidate)}
        p="10px"
        gap="14px"
        background={
          isSelected ? "gray.800" : "transparent"
        }
        alignItems="center"
        borderBottom="1px solid"
        borderColor="gray.900"
        borderLeft="3px solid"
        borderLeftColor={recommended ? RECOMMENDED_ACCENT : "transparent"}
        data-imported={isImported ? "true" : undefined}
        data-recommended={recommended ? "true" : undefined}
        data-testid="candidate-report-row"
      >
        <ProfileImage
          imageUrl={candidate?.profileImageUrl}
          name={candidate?.fullName}
          size="60px"
          mr="2"
          ml="2"
          opacity={importedDim}
        />
        <Flex flex={1} direction="column" justifyContent={"center"} gap="4px" opacity={importedDim}>
          <HStack spacing="6px" minW={0} data-testid="candidate-name">
            <Text fontSize="md" fontStyle={isImported ? "italic" : "normal"}>{candidate?.fullName}</Text>
            {recommended && <RecommendedStar />}
          </HStack>
          <AvailabilityLine profile={candidate?.candidateProfile} fontSize="xs" />
          {recommended && <RecommendedNote candidateReport={candidateReport} clamp />}
          {showRejectedLabel && (
            <Tag
              size="sm"
              colorScheme="red"
              variant="subtle"
              textTransform="uppercase"
              fontSize="10px"
              alignSelf="flex-start"
            >
              Rejected
            </Tag>
          )}
          {candidateReport?.isInternalTestUser && (
            <Tag
              size="sm"
              colorScheme="purple"
              variant="subtle"
              textTransform="uppercase"
              fontSize="10px"
              alignSelf="flex-start"
            >
              Test account
            </Tag>
          )}
        </Flex>
        {renderClientShortlistStatus({ candidate, candidateReport })}
      </Flex>
    );
  };

  const renderCandidateTriageListItem = ({ candidate, candidateTriage }) => {
    if (!candidateTriage || !candidate) return null;

    return (
      <Flex
        key={candidateTriage.id}
        cursor="pointer"
        onClick={() => setSelect(_.cloneDeep(candidateTriage))}
        p="30px 14px"
        gap="14px"
        background={candidateTriage.id === select?.id && "#2C323D"}
      >
        <ProfileImage
          imageUrl={candidate?.profileImageUrl}
          name={candidate?.fullName}
          size="60px"
          mr="2"
          ml="2"
        />
        <Flex flex={1} direction="column" justifyContent={"center"}>
          <Text fontSize="lg" fontWeight="semibold">
            {candidate?.fullName}
          </Text>
          <AvailabilityLine profile={candidate?.candidateProfile} />
        </Flex>
        {renderClientShortlistStatus({ candidate, candidateTriage })}
      </Flex>
    );
  };

  const showFilterDrawer =
    showFavourite || criteriaCategories.length > 0 || showAvailabilityFilter;

  return (
    <Stack
      w="435px"
      h="100%"
      direction="column"
      bgColor="black"
      borderRadius="12px"
      minHeight="0"
      spacing={0}
    >
      <HStack p="10px" justifyContent="space-between" alignItems="center">
        <Text fontSize="18px" color="whiteAlpha.900">
          Candidates
          {showFilterDrawer && (
            <Text as="span" ml="6px">
              (Showing {filteredCandidates}/{totalCandidates})
            </Text>
          )}
        </Text>
        <HStack spacing={2} alignItems="center">
          {showFavourite && invitableCandidateReportList.length > 0 && (
            <Button
              size="xs"
              colorScheme="teal"
              variant="outline"
              onClick={onInviteAllOpen}
            >
              Invite All ({invitableCandidateReportList.length})
            </Button>
          )}
          {showFilterDrawer && (
            <HStack
              spacing={1}
              px={2}
              py={1}
              borderRadius="md"
              cursor="pointer"
              color={hasActiveFilters ? "teal.300" : "whiteAlpha.700"}
              _hover={{ color: "white", bg: "whiteAlpha.100" }}
              onClick={() => setFiltersOpen(!filtersOpen)}
              role="button"
              aria-label="Toggle filters"
            >
              <Icon as={SlidersHorizontalIcon} boxSize="16px" />
              <Text fontSize="sm">Filters</Text>
              <ChevronDownIcon
                boxSize="16px"
                transform={filtersOpen ? "rotate(180deg)" : "none"}
                transition="transform 0.2s"
              />
            </HStack>
          )}
        </HStack>
      </HStack>
      {filtersOpen && showFilterDrawer && (
        <Stack
          p="10px"
          gap="16px"
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="gray.700"
          maxH="30vh"
          overflowY="auto"
          flexShrink={0}
          sx={{
            background: [
              "linear-gradient(black 30%, rgba(0,0,0,0)) center top",
              "linear-gradient(rgba(0,0,0,0), black 70%) center bottom",
              "radial-gradient(farthest-side at 50% 0, rgba(255,255,255,0.35), rgba(255,255,255,0)) center top",
              "radial-gradient(farthest-side at 50% 100%, rgba(255,255,255,0.35), rgba(255,255,255,0)) center bottom",
            ].join(", "),
            backgroundColor: "black",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% 28px, 100% 28px, 100% 16px, 100% 16px",
            backgroundAttachment: "local, local, scroll, scroll",
          }}
        >
          {/* "Show only" toggles. Favorites is intrinsically a
              Suggested-tab concept (gated on showFavourite), but the
              "Moblyze users only" source filter applies to every list that
              can contain external candidates, so it's shown whenever the
              filter drawer is open — not just on the Suggested tab. */}
          <Stack gap="8px">
            <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.900" textTransform="uppercase">
              Show only
            </Text>
            {showFavourite && (
              <HStack spacing={3} pl="4px" align="center">
                <Switch
                  size="sm"
                  colorScheme="teal"
                  isChecked={favouritesOnly}
                  onChange={() => setFavouritesOnly(!favouritesOnly)}
                  aria-label="Show only favorites"
                />
                <Text fontSize="sm" color="white" flex="1">Favorites</Text>
              </HStack>
            )}
            <HStack spacing={3} pl="4px" align="center">
              <Switch
                size="sm"
                colorScheme="teal"
                isChecked={moblyzeUsersOnly}
                onChange={() => setMoblyzeUsersOnly(!moblyzeUsersOnly)}
                aria-label="Show only Moblyze users"
              />
              <Text fontSize="sm" color="white" flex="1">Moblyze users only</Text>
            </HStack>
          </Stack>
          {availabilityOptions.length > 0 && (
            <Stack gap="8px">
              <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.900" textTransform="uppercase">
                Availability
              </Text>
              <RadioGroup
                value={availabilityFilter}
                onChange={setAvailabilityFilter}
                aria-label="Availability"
              >
                <Stack gap="8px" pl="4px">
                  {availabilityOptions.map((option) => (
                    <HStack key={option.value} spacing={3} align="center">
                      <Radio size="sm" colorScheme="teal" value={option.value} flex="1">
                        <Text fontSize="sm" color="white">
                          {option.label}
                        </Text>
                      </Radio>
                      <Text
                        fontSize="sm"
                        color="whiteAlpha.700"
                        minW="24px"
                        textAlign="right"
                        data-testid={`availability-count-${option.value}`}
                      >
                        {option.count}
                      </Text>
                      <Box w="16px" />
                    </HStack>
                  ))}
                </Stack>
              </RadioGroup>
              <Text fontSize="xs" color="whiteAlpha.600" pl="4px">
                Counts candidates who have told us. Anyone we have not asked is not shown as available.
              </Text>
            </Stack>
          )}
          {criteriaCategories.length > 0 && moblyzeJobId && (
            <Stack gap="8px">
              <HStack justify="space-between" align="center">
                <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.900" textTransform="uppercase">
                  Must match
                </Text>
                {allowEditCriteria && (
                  <HStack
                    spacing={1}
                    px={2}
                    py={1}
                    borderRadius="md"
                    cursor="pointer"
                    color="whiteAlpha.800"
                    _hover={{ color: "white", bg: "whiteAlpha.100" }}
                    onClick={onEditReqsOpen}
                    role="button"
                    aria-label="Edit job requirements"
                  >
                    <EditIcon boxSize="12px" />
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase">
                      Edit
                    </Text>
                  </HStack>
                )}
              </HStack>
              {criteriaCategories.map((category) => (
                <Stack key={category.slug} gap="8px">
                  <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.700" textTransform="uppercase" pl="4px">
                    {category.name}
                  </Text>
                  <Stack gap="8px">
                    {category.criteria.map((req) => {
                      const isActive = criterionFilters.includes(req.id);
                      const matchCount = criterionMatchCounts[req.id] || 0;
                      return (
                        <HStack key={req.id} spacing={3} pl="4px" align="center">
                          <Switch
                            size="sm"
                            colorScheme="teal"
                            isChecked={isActive}
                            onChange={() => {
                              setCriterionFilters((prev) =>
                                isActive
                                  ? prev.filter((id) => id !== req.id)
                                  : [...prev, req.id]
                              );
                            }}
                          />
                          <Box flex="1" minW="0">
                            <Tooltip
                              label={req.description}
                              isDisabled={!req.description}
                              hasArrow
                              placement="top"
                            >
                              <Text fontSize="sm" color="white" noOfLines={1}>
                                {req.title}
                              </Text>
                            </Tooltip>
                          </Box>
                          <Text
                            fontSize="sm"
                            color="whiteAlpha.700"
                            minW="24px"
                            textAlign="right"
                          >
                            {matchCount}
                          </Text>
                          <Box w="16px" display="flex" justifyContent="center">
                            {req.description && (
                              <Popover trigger="click" placement="top" isLazy>
                                <PopoverTrigger>
                                  <Box
                                    as="button"
                                    type="button"
                                    display="inline-flex"
                                    alignItems="center"
                                    color="whiteAlpha.700"
                                    _hover={{ color: "white" }}
                                    aria-label="Show description"
                                  >
                                    <InfoOutlineIcon boxSize="12px" />
                                  </Box>
                                </PopoverTrigger>
                                <PopoverContent bg="gray.700" color="white" border="none" maxW="280px">
                                  <PopoverArrow bg="gray.700" />
                                  <PopoverBody fontSize="sm">{req.description}</PopoverBody>
                                </PopoverContent>
                              </Popover>
                            )}
                          </Box>
                        </HStack>
                      );
                    })}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
          {hasActiveFilters && (
            <HStack
              spacing={1}
              alignSelf="flex-start"
              color="teal.300"
              cursor="pointer"
              _hover={{ color: "teal.200" }}
              onClick={() => {
                setFavouritesOnly(false);
                setMoblyzeUsersOnly(false);
                setCriterionFilters([]);
                setAvailabilityFilter(AVAILABILITY_FILTER_ANY);
              }}
              role="button"
              aria-label="Clear filters"
            >
              <RepeatIcon boxSize="12px" />
              <Text fontSize="xs" textDecoration="underline">
                Clear filters
              </Text>
            </HStack>
          )}
        </Stack>
      )}
      {typeof matchingProgress === "number" && matchingProgress < 0.2 && (
        <Box
          position="relative"
          h="24px"
          borderTopWidth="1px"
          borderTopStyle="solid"
          borderTopColor="gray.700"
          borderBottomWidth="1px"
          borderBottomStyle="solid"
          borderBottomColor="gray.700"
        >
          <Progress
            value={displayedMatchingProgress(matchingProgress) * 100}
            h="24px"
            colorScheme="teal"
            bg="whiteAlpha.100"
            borderRadius="0"
            hasStripe
            isAnimated
            aria-label="Matching candidates in progress"
          />
          <Flex
            position="absolute"
            inset="0"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            <Text fontSize="xs" fontWeight="semibold" color="white">
              Searching for candidates
            </Text>
          </Flex>
        </Box>
      )}
      <Flex
        position="relative"
        w="435px"
        h="100%"
        direction="column"
        overflowY="auto"
        minHeight="0"
      >
        {totalCandidates > 0 && filteredCandidates === 0 && hasActiveFilters && (
          <Stack p="16px" gap="4px">
            <Text fontSize="sm" color="whiteAlpha.800">
              No candidates match your filters.{" "}
              <Text
                as="span"
                fontStyle="italic"
                textDecoration="underline"
                cursor="pointer"
                color="teal.300"
                onClick={() => setFiltersOpen(true)}
              >
                Update your filters
              </Text>
            </Text>
          </Stack>
        )}
        {recommendedFirst(
          invitedCandidateReportList.concat(sortedCandidateReportList)
        ).map(({ candidateReport, candidate }) =>
            renderCandidateReportListItem({
              candidate,
              candidateReport,
            })
          )}
        {sortedCandidateTriageList.map(({ candidate, candidateTriage }) =>
          renderCandidateTriageListItem({
            candidate,
            candidateTriage,
          })
        )}
      </Flex>
      <Modal isOpen={isInviteAllOpen} onClose={isInviting ? undefined : onInviteAllClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.900" color="white">
          <ModalHeader>
            {!isInviting && <ModalCloseButton />}
            Invite All Candidates
          </ModalHeader>
          <ModalBody pb={2}>
            {!isInviting ? (
              <Text fontSize="sm">
                You are about to notify{" "}
                <Text as="span" fontWeight="bold">
                  {invitableCandidateReportList.length} candidate{invitableCandidateReportList.length !== 1 ? "s" : ""}
                </Text>
                . Invited jobs appear first in each candidate's feed.
              </Text>
            ) : (
              <Stack spacing={3}>
                <Text fontSize="sm">
                  Inviting candidates… {Math.round(inviteAllProgress * invitableCandidateReportList.length)}/{invitableCandidateReportList.length}
                </Text>
                <Progress
                  value={inviteAllProgress * 100}
                  colorScheme="teal"
                  bg="whiteAlpha.200"
                  borderRadius="md"
                  hasStripe
                  isAnimated
                />
              </Stack>
            )}
          </ModalBody>
          {!isInviting && (
            <ModalFooter gap={2}>
              <Button colorScheme="teal" onClick={onInviteAll}>
                Send Invites
              </Button>
              <Button variant="outline" colorScheme="whiteAlpha" onClick={onInviteAllClose}>
                Cancel
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      {moblyzeJobId && allowEditCriteria && (
        <Modal
          isOpen={isEditReqsOpen}
          onClose={() => {
            onEditReqsClose();
            // Editing criteria re-runs matching on the backend, so kick a
            // refetch now to pick up the updated criteria + matching status.
            if (refetch) refetch();
          }}
          size="2xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent bg="gray.900" color="white">
            <ModalHeader>Edit Matching Criteria</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Text fontSize="sm" color="whiteAlpha.900" mb={4}>
                These requirements will be used to filter and screen candidates (they don&apos;t appear on the job post).
              </Text>
              <JobRequirementsEditor moblyzeJobId={moblyzeJobId} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Stack>
  );
}

export default CandidateListItem;
